import { ColumnDef } from '@tanstack/react-table';
import {
  MoreHorizontal,
  Eye,
  Clock,
  CheckCircle,
  Trash2,
  ShieldMinus,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable';
import { Account, DateRange } from '@/types';
import { formatDate } from '@/utils';
import {
  useGetAccountsQuery,
  useUpdateCategoryMutation,
  useUpdateUserStatusMutation,
} from '@/store/api/accountsApi';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { DateRangePicker } from '@/components/common/DateRangePicker';

interface KycStatusBadgeProps {
  isVerified: boolean;
}

function UserActions({ user }: { user: Account }) {
  const [updateStatus, { isLoading }] = useUpdateUserStatusMutation();

  const handleStatusUpdate = async (
    payload: { isDeleted?: true } | { status?: 'Active' | 'Blocked' }
  ) => {
    try {
      await updateStatus({
        accountId: Number(user.id),
        ...payload,
      }).unwrap();
    } catch (err) {
      console.error('Failed to update user status', err);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" disabled={isLoading}>
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {user.status === 'Active' ? (
          <>
            <DropdownMenuItem onClick={() => handleStatusUpdate({ status: 'Blocked' })}>
              <ShieldMinus className="mr-2 h-4 w-4" />
              Inactive
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem onClick={() => handleStatusUpdate({ status: 'Active' })}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Active
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type UserStatus = 'Active' | 'Blocked';

function StatusBadge({ status }: { status: UserStatus }) {
  const config: Record<
    UserStatus,
    {
      label: string;
      container: string;
      dot: string;
    }
  > = {
    Active: {
      label: 'Active',
      container: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      dot: 'bg-green-400',
    },
    Blocked: {
      label: 'Blocked',
      container: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      dot: 'bg-red-400',
    },
  };

  const { label, container, dot } = config[status];

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${container}`}
    >
      <span className={`mr-1 h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export function KycStatusBadge({ isVerified }: KycStatusBadgeProps) {
  if (isVerified) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
        <CheckCircle className="h-3 w-3" />
        KYC Verified
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
      <Clock className="h-3 w-3" />
      KYC Pending
    </span>
  );
}

export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { userType } = useParams();
  const [updateCategory] = useUpdateCategoryMutation();
  const { data, isLoading, isError } = useGetAccountsQuery(
    {
      skip: pageIndex * pageSize,
      take: pageSize,
      search: debouncedSearch,
      type: userType,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0); // 👈 always reset to first page
  };
  const users: Account[] = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const columns: ColumnDef<Account>[] = [
    {
      header: 'Name',
      cell: ({ row }) => {
        const user = row.original;
        const fullName = `${user.firstname} ${user.lastname}`.trim();

        return (
          <div className="flex items-center space-x-3">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium overflow-hidden
    ${
      user?.profileImage
        ? 'border border-primary bg-transparent'
        : 'bg-primary text-primary-foreground'
    }`}
            >
              {user?.profileImage ? (
                <img
                  src={user.profileImage}
                  alt="profile"
                  className="h-full w-full object-cover rounded-full"
                />
              ) : (
                user?.firstname?.charAt(0).toUpperCase()
              )}
            </div>

            <div>
              <div className="font-medium">{fullName}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Mobile No.',
      cell: ({ row }) => <span className="capitalize">{row.original.mobile}</span>,
    },

    {
      header: 'Role',
      cell: ({ row }) => (
        <span className="capitalize">{row.original.type?.toLowerCase() || '—'}</span>
      ),
    },

    {
      header: 'Details',
      cell: ({ row }) => (
        <div
          onClick={() => navigate(`/user-detail/${row?.original?.id}`)}
          className="w-full flex items-center  cursor-pointer"
        >
          <Eye className="h-4 w-4" />
        </div>
      ),
    },
    {
      header: 'Status',
      cell: ({ row }) =>
        row.original.status ? (
          <StatusBadge status={row.original.status as 'Active' | 'Blocked'} />
        ) : (
          <span>—</span>
        ),
    },

    {
      header: 'Category',
      cell: ({ row }) => {
        const user = row.original;
        type userType = 'Tribe' | 'Member';
        const teams: { value: userType; label: string; icon: string }[] = [
          { value: 'Tribe', label: 'TRIBE', icon: '🟡' },
          { value: 'Member', label: 'MEMBER', icon: '🔵' },
        ];
        const selectedTeam = teams.find((t) => t.value === user.type);
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-8 px-2 text-sm" disabled={isLoading}>
                {selectedTeam
                  ? `${selectedTeam.icon} ${selectedTeam.label}`
                  : // : user.role == 'MEMBER'
                    //   ? 'MEMBER 🔴'
                    'Select Category'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              <DropdownMenuLabel>Select</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {teams.map((team) => (
                <DropdownMenuItem
                  key={team.value}
                  onClick={() => handleCategoryUpdate(team.value, user.id)}
                >
                  {team.icon} {team.label} {team.value === user.type && '✓'}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },

    {
      header: 'Created',
      cell: ({ row }) => <span>{formatDate(new Date(row.original.createdAt))}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <UserActions user={row.original} />,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  const handleCategoryUpdate = async (type: 'Tribe' | 'Member', userId: number) => {
    try {
      await updateCategory({
        userId: Number(userId),
        type,
      }).unwrap();
      console.log('Selected team:', type, 'User:', userId);
    } catch (err) {
      console.error('Failed to update team', err);
    }
  };

  const handleDelete = (selectedUsers: Account[]) => {
    if (confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      alert(`${selectedUsers.length} users deleted!`);
    }
  };
  if (isLoading) {
    return <div className="p-6">Loading users...</div>;
  }

  // if (isError) {
  //   return <div className="p-6 text-red-500">Failed to load users</div>;
  // }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap md:items-center flex-col md:flex-row gap-4 md:gap-10 w-full">
          <h2 className="text-xl lg:text-2xl xl:text-3xl font-bold tracking-tight">User Listing</h2>
        </div>
        {/* <Button onClick={() => navigate(`/create-user/${userType}`)}>Add User</Button> */}
      </div>

      <DataTable
        data={users}
        columns={columns}
        pageSize={pageSize}
        pageIndex={pageIndex}
        pageCount={Math.ceil(totalCount / pageSize)}
        manualPagination
        onDelete={handleDelete}
        enableFilters={false}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
}
